import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import './UpdateNotification.css';

function applyCachedUpdateStatus(status, setUpdateState) {
  if (!status || status.phase === 'idle') {
    return;
  }

  switch (status.phase) {
    case 'available':
      setUpdateState(prev => ({
        ...prev,
        available: true,
        version: status.version,
        releaseNotes: status.releaseNotes,
        error: null,
      }));
      break;
    case 'downloading':
      setUpdateState(prev => ({
        ...prev,
        available: true,
        downloading: true,
        version: status.version,
        releaseNotes: status.releaseNotes,
        progress: status.progress,
        error: null,
      }));
      break;
    case 'downloaded':
      setUpdateState(prev => ({
        ...prev,
        available: true,
        downloading: false,
        downloaded: true,
        version: status.version,
        releaseNotes: status.releaseNotes,
        error: null,
      }));
      break;
    case 'error':
      setUpdateState(prev => ({
        ...prev,
        error: status.message,
        downloading: false,
      }));
      break;
    default:
      break;
  }
}

/**
 * UpdateNotification Component
 * Displays update notifications and progress for Electron app updates
 */
function UpdateNotification() {
  const [updateState, setUpdateState] = useState({
    available: false,
    downloading: false,
    downloaded: false,
    error: null,
    version: null,
    releaseNotes: null,
    progress: null
  });

  useEffect(() => {
    if (!window.electron || !window.electron.updater) {
      return;
    }

    const { updater } = window.electron;
    let cancelled = false;

    const handleUpdateAvailable = (data) => {
      console.log('Update available:', data.version);
      setUpdateState(prev => ({
        ...prev,
        available: true,
        version: data.version,
        releaseNotes: data.releaseNotes,
        error: null
      }));
    };

    const handleUpdateNotAvailable = () => {
      setUpdateState(prev => ({
        ...prev,
        available: false
      }));
    };

    const handleDownloadProgress = (data) => {
      console.log('Download progress:', data.percent + '%');
      setUpdateState(prev => ({
        ...prev,
        available: true,
        downloading: true,
        progress: data.percent
      }));
    };

    const handleUpdateDownloaded = (data) => {
      console.log('Update downloaded:', data.version);
      setUpdateState(prev => ({
        ...prev,
        downloading: false,
        downloaded: true,
        version: data.version,
        releaseNotes: data.releaseNotes
      }));
      // Main process owns silent install via scheduleSilentInstallAfterDownload().
    };

    const handleUpdateError = (data) => {
      console.error('Update error:', data.message);
      setUpdateState(prev => ({
        ...prev,
        error: data.message,
        downloading: false
      }));
    };

    updater.onUpdateAvailable(handleUpdateAvailable);
    updater.onUpdateNotAvailable(handleUpdateNotAvailable);
    updater.onUpdateDownloadProgress(handleDownloadProgress);
    updater.onUpdateDownloaded(handleUpdateDownloaded);
    updater.onUpdateError(handleUpdateError);

    const syncCachedStatus = async () => {
      if (typeof updater.getUpdateStatus !== 'function') {
        updater.notifyRendererReady();
        return;
      }
      try {
        const status = await updater.getUpdateStatus();
        if (!cancelled) {
          applyCachedUpdateStatus(status, setUpdateState);
        }
      } catch (err) {
        console.error('Failed to sync cached update status:', err);
      } finally {
        if (!cancelled && typeof updater.notifyRendererReady === 'function') {
          updater.notifyRendererReady();
        }
      }
    };

    syncCachedStatus();

    return () => {
      cancelled = true;
      updater.removeAllListeners('update-available');
      updater.removeAllListeners('update-not-available');
      updater.removeAllListeners('update-download-progress');
      updater.removeAllListeners('update-downloaded');
      updater.removeAllListeners('update-error');
    };
  }, []);

  const handleDismiss = () => {
    setUpdateState(prev => ({
      ...prev,
      available: false,
      error: null
    }));
  };

  if (!window.electron || !window.electron.updater) {
    return null;
  }

  let overlay = null;

  if (updateState.error) {
    overlay = (
      <div className="update-notification error">
        <div className="update-notification-content">
          <div className="update-notification-header">
            <span className="update-notification-title">Update Error</span>
            <button className="update-notification-close" onClick={handleDismiss}>×</button>
          </div>
          <div className="update-notification-body">
            <p>{updateState.error}</p>
          </div>
        </div>
      </div>
    );
  } else if (updateState.available && !updateState.downloading && !updateState.downloaded) {
    overlay = (
      <div className="update-lock-overlay">
        <div className="update-lock-content">
          <div className="update-lock-header">
            <h2 className="update-lock-title">Update Available</h2>
            <p className="update-lock-subtitle">Preparing to download update v{updateState.version}...</p>
          </div>
        </div>
      </div>
    );
  } else if (updateState.downloading) {
    overlay = (
      <div className="update-lock-overlay">
        <div className="update-lock-content">
          <div className="update-lock-header">
            <h2 className="update-lock-title">Updating Game</h2>
            <p className="update-lock-subtitle">Please wait while we download the latest version...</p>
          </div>
          <div className="update-lock-progress-container">
            <div className="update-lock-progress-bar">
              <div
                className="update-lock-progress-fill"
                style={{ width: `${updateState.progress || 0}%` }}
              />
            </div>
            <p className="update-lock-progress-text">
              {Math.round(updateState.progress || 0)}% complete
            </p>
            {updateState.progress !== null && updateState.progress > 0 && (
              <p className="update-lock-progress-details">
                Downloading update v{updateState.version}...
              </p>
            )}
          </div>
        </div>
      </div>
    );
  } else if (updateState.downloaded) {
    overlay = (
      <div className="update-lock-overlay">
        <div className="update-lock-content">
          <div className="update-lock-header">
            <h2 className="update-lock-title">Update Ready</h2>
            <p className="update-lock-subtitle">Installing update v{updateState.version}...</p>
          </div>
          <div className="update-lock-progress-container">
            <div className="update-lock-progress-bar">
              <div className="update-lock-progress-fill" style={{ width: '100%' }} />
            </div>
            <p className="update-lock-progress-text">The game will restart automatically...</p>
            <p className="update-lock-progress-details">
              (Installer runs silently in the background)
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!overlay || typeof document === 'undefined') {
    return null;
  }

  const portalTarget = document.getElementById('coda-update-overlay-root') || document.body;

  return createPortal(
    <div className="coda-update-overlay-host" style={{ position: 'fixed', inset: 0, zIndex: 2147483647, pointerEvents: 'auto' }}>
      {overlay}
    </div>,
    portalTarget
  );
}

export default UpdateNotification;
